import { useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';

import type { TaxInfo, BillingInfoFormValues } from '@ctct/application-layer';
import { type BillingStrategyQuery, isStripeBillingStrategy } from '@ctct/domain-layer';

import { resolveBillingStrategyQuery } from '../../helpers/resolveStrategyQuery';

interface TaxInfoUpdateDetectorProps {
  onTaxInfoUpdate: (taxInfo: TaxInfo) => void;
  billingStrategy: BillingStrategyQuery;
}

// This react component does not render anything
// It is responible for detecing tax info update and calling the right handler
export const TaxInfoUpdateDetector = ({ onTaxInfoUpdate, billingStrategy }: TaxInfoUpdateDetectorProps) => {
  const address1 = useWatch<BillingInfoFormValues>({ name: 'address1' }) as string | undefined;
  const city = useWatch<BillingInfoFormValues>({ name: 'city' }) as string | undefined;
  const zipcode = useWatch<BillingInfoFormValues>({ name: 'zipcode' }) as string | undefined;
  const state = useWatch<BillingInfoFormValues>({ name: 'state' }) as string | undefined;
  const selectedCountryCode = useWatch<BillingInfoFormValues>({ name: 'country' }) as string | undefined;
  const phone = useWatch<BillingInfoFormValues>({ name: 'phone' }) as string | undefined;

  const newValues = { selectedCountryCode, city, zipcode, state, address1, phone };
  const prevValuesRef = useRef(newValues);
  const isStripe = isStripeBillingStrategy(resolveBillingStrategyQuery(billingStrategy));

  const hasChanged = (key: keyof typeof newValues) => prevValuesRef.current[key] !== newValues[key];

  // Skip tax info update when country just changed to prevent sending stale address data
  // The form resets address data on country change, but the reset happens in a separate effect
  const countryJustChanged = hasChanged('selectedCountryCode');

  useEffect(() => {
    // Detect if only phone changed (all other fields stayed the same)
    const onlyPhoneChanged =
      hasChanged('phone') &&
      !(['selectedCountryCode', 'city', 'zipcode', 'state', 'address1'] as const).some(hasChanged);

    // Update ref for next comparison
    prevValuesRef.current = newValues;

    // Skip when country just changed - wait for other address data state/province, city, zipcode etc to be reset first
    if (countryJustChanged) {
      return;
    }

    // For non-Stripe accounts, don't trigger a ratings call when only phone changes
    // For Stripe accounts, phone changes trigger ratings calls (phone is stored at rating time).
    if (!isStripe && onlyPhoneChanged) {
      return;
    }

    // Only trigger rating call if all required fields exist
    // Zipcode validation is handled by the onTaxInfoUpdate handler
    if (address1 && city && zipcode && selectedCountryCode) {
      onTaxInfoUpdate({
        address1,
        city,
        zipcode,
        state,
        country: selectedCountryCode,
        ...(phone ? { phone } : undefined),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasChanged and newValues should not be included in the deps array
  }, [selectedCountryCode, city, zipcode, state, address1, phone, onTaxInfoUpdate, isStripe, countryJustChanged]);

  return null;
};
